import { Stack, Typography } from '@mui/material';
import { ComponentType, useCallback, useMemo, useState } from 'react';

import { BoxedIcon } from '@/components/display/Ribbon/BoxedIcon';
import { Button } from '@/components/form/baseInputs/Button';
import { IconProps } from '@/components/icons/ChevronDownIcon';
import { CurrencyDollarIcon } from '@/components/icons/CurrencyDollarIcon';
import { PlusIcon } from '@/components/icons/PlusIcon';
import { User01Icon } from '@/components/icons/User01Icon';
import { Users01Icon } from '@/components/icons/Users01Icon';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { ContextualHelpTrigger } from '@/modules/content/components/ContextualHelpTrigger';
import { useEstateHoldingsContext } from '@/modules/estate/contexts/estateHoldings.context';
import { DirectHoldingsGroupCard } from '@/modules/estate/EstateDirectHoldings/DirectHoldingsGroupContainer/DirectHoldingsGroupCard/DirectHoldingsGroupCard';
import { COLORS } from '@/styles/tokens/colors';
import { formatCurrencyNoDecimals } from '@/utils/formatting/currency';

import { EditEntitySection } from '../../../entities/EditEntitySplitScreen/EditEntitySplitScreen.types';
import { ContextPrimaryClient } from '../../../household/contexts/householdDetails.context';
import { DirectHoldingsGroupVariant } from '../EstateDirectHoldings.types';
import {
  computeDirectHoldingsSectionTotal,
  DIRECT_HOLDINGS_CARDS_BY_VARIANT,
} from '../EstateDirectHoldings.utils';
import { AddNewHoldingsGroupFormModal } from './AddNewHoldingsGroupFormModal/AddNewHoldingsGroupFormModal';

export interface DirectHoldingsGroupContainerProps {
  clientProfile?: ContextPrimaryClient;
  onEditEntitySection?: (entityId: string, section: EditEntitySection) => void;
  suggestionsOnly?: boolean;
  variant: DirectHoldingsGroupVariant;
  primaryClients?: ContextPrimaryClient[];
}

const DirectHoldingsGroupVariantIcon: Record<
  DirectHoldingsGroupVariant,
  ComponentType<IconProps>
> = {
  individual: User01Icon,
  joint: Users01Icon,
  other: CurrencyDollarIcon,
};

export function DirectHoldingsGroupContainer({
  clientProfile,
  onEditEntitySection,
  suggestionsOnly = false,
  variant,
  primaryClients,
}: DirectHoldingsGroupContainerProps) {
  const canEdit = useCanEditHouseholdData();
  const { entities } = useEstateHoldingsContext();
  const [addNewHoldingsGroupModalOpen, setAddNewHoldingsGroupModalOpen] =
    useState(false);

  const clickAddNewHoldingsGroup = useCallback(() => {
    setAddNewHoldingsGroupModalOpen(true);
  }, []);

  let title;
  if (variant === 'individual') {
    if (!clientProfile) {
      throw new Error('ClientProfile required');
    }
    title = `${clientProfile!.displayName}`;
  } else if (variant === 'joint') {
    title = 'Joint holdings';
  } else if (variant === 'other') {
    title = 'Other holdings';
  }

  // The total is derived from the same `DIRECT_HOLDINGS_CARDS_BY_VARIANT` map
  // the cards below are rendered from, so it always reflects what's displayed.
  const sectionTotal = useMemo(
    () =>
      computeDirectHoldingsSectionTotal(entities ?? [], {
        variant,
        clientProfileId: clientProfile?.id,
        primaryClientIds: primaryClients?.map((client) => client.id),
      }),
    [entities, variant, clientProfile, primaryClients]
  );

  return (
    <Stack data-testid={`holdings-group-container-${variant}`}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        gap={1}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <BoxedIcon
            icon={DirectHoldingsGroupVariantIcon[variant]}
            sx={{
              backgroundColor: COLORS.NAVY[100],
            }}
            iconProps={{
              size: 24,
              color: COLORS.NAVY[700],
              strokeWidth: 1.67,
            }}
          />
          <Stack>
            <Stack direction="row" alignItems="center" gap={0.5}>
              <Typography variant="title-s" component="div">
                {title}
              </Typography>
              {!suggestionsOnly && (
                <ContextualHelpTrigger contextualHelpId="directHoldingsGroups" />
              )}
            </Stack>
            <Typography
              variant="body-xs"
              sx={{ fontSize: '12px' }}
              component="span"
              data-testid="holdings-group-section-total"
            >
              {formatCurrencyNoDecimals(sectionTotal)}
            </Typography>
          </Stack>
        </Stack>
        {canEdit && !suggestionsOnly && (
          <Button
            variant="transparent"
            size="xs"
            startIcon={PlusIcon}
            onClick={clickAddNewHoldingsGroup}
            data-testid={`button-add-holdings-group-${variant}`}
          >
            Holdings group
          </Button>
        )}
      </Stack>
      <Stack gap={1} mt={2}>
        {DIRECT_HOLDINGS_CARDS_BY_VARIANT[variant].map((cardVariant) => (
          <DirectHoldingsGroupCard
            key={cardVariant}
            variant={cardVariant}
            onEditEntitySection={onEditEntitySection}
            suggestionsOnly={suggestionsOnly}
            // `individual` containers scope cards to a single client;
            // `other` containers pass the primary clients so accounts owned by
            // none of them are surfaced. Custodial/529 cards ignore both.
            clientProfile={variant === 'individual' ? clientProfile : undefined}
            primaryClients={variant === 'other' ? primaryClients : undefined}
          />
        ))}
      </Stack>
      <AddNewHoldingsGroupFormModal
        clientProfile={clientProfile}
        variant={variant}
        open={addNewHoldingsGroupModalOpen}
        onClose={() => setAddNewHoldingsGroupModalOpen(false)}
      />
    </Stack>
  );
}
